'use client';

import { VoteType } from '@prisma/client';
import { motion, useAnimation } from 'framer-motion';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

type TrafficVoteButtonsProps = {
  likes: number;
  dislikes: number;
  myVote: VoteType | null; // Обязательный пропс теперь
  onVote: (voteType: VoteType) => void;
  variant?: 'card' | 'modal';
};

export const TrafficVoteButtons = memo(function TrafficVoteButtons({
  likes,
  dislikes,
  myVote,
  onVote,
  variant = 'modal',
}: TrafficVoteButtonsProps) {
  const { data: session } = useSession();
  const [isVoting, setIsVoting] = useState(false);
  const likeControls = useAnimation();
  const dislikeControls = useAnimation();
  const prevVote = useRef(myVote);
  const cooldownRef = useRef(false);
  
  useEffect(() => {
    if (myVote === 'LIKE' && prevVote.current !== 'LIKE')
      likeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.25 } });
    if (myVote === 'DISLIKE' && prevVote.current !== 'DISLIKE')
      dislikeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.25 } });
    prevVote.current = myVote;
  }, [myVote, likeControls, dislikeControls]);

  const handleVote = useCallback((e: React.MouseEvent, type: VoteType) => {
    e.stopPropagation();
    if (!session?.user) return toast.error('Войдите, чтобы голосовать');
    if (cooldownRef.current) return;
  
    cooldownRef.current = true;
    window.setTimeout(() => (cooldownRef.current = false), 500);
  
    onVote(type);
  }, [session, onVote]);

  const isCard = variant === 'card';

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        !isCard && 'bg-slate-800/80 p-1.5 rounded-full border border-slate-700/50',
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              onClick={(e) => handleVote(e, 'LIKE')}
              whileTap={{ scale: 0.9 }}
              className={cn(
                'flex items-center gap-2 rounded-full transition-all',
                isCard ? 'px-2 py-1' : 'px-3 py-1.5',
                myVote === 'LIKE' ? 'bg-sky-500/20 hover:bg-sky-500/30' : 'hover:bg-slate-700/50',
              )}
            >
              <motion.div animate={likeControls}>
                <ThumbsUp
                  size={isCard ? 16 : 18}
                  className={myVote === 'LIKE' ? 'text-sky-400' : 'text-slate-400'}
                  fill={myVote === 'LIKE' ? 'currentColor' : 'none'}
                />
              </motion.div>
              <span
                className={cn(
                  'font-bold text-sm',
                  myVote === 'LIKE' ? 'text-sky-100' : 'text-slate-300',
                )}
              >
                {likes}
              </span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent>{myVote === 'LIKE' ? 'Отменить' : 'Подтвердить'}</TooltipContent>
        </Tooltip>

        <div className="w-px h-5 bg-slate-600/50 mx-1" />

        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              onClick={(e) => handleVote(e, 'DISLIKE')}
              whileTap={{ scale: 0.9 }}
              className={cn(
                'flex items-center gap-2 rounded-full transition-all',
                isCard ? 'px-2 py-1' : 'px-3 py-1.5',
                myVote === 'DISLIKE'
                  ? 'bg-rose-500/20 hover:bg-rose-500/30'
                  : 'hover:bg-slate-700/50',
              )}
            >
              <motion.div animate={dislikeControls}>
                <ThumbsDown
                  size={isCard ? 16 : 18}
                  className={myVote === 'DISLIKE' ? 'text-rose-400' : 'text-slate-400'}
                  fill={myVote === 'DISLIKE' ? 'currentColor' : 'none'}
                />
              </motion.div>
              <span
                className={cn(
                  'font-bold text-sm',
                  myVote === 'DISLIKE' ? 'text-rose-100' : 'text-slate-300',
                )}
              >
                {dislikes}
              </span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent>{myVote === 'DISLIKE' ? 'Отменить' : 'Не актуально'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});
